/**
 * Centralized clipboard utility functions
 */
/**
 * Fallback method to copy text using document.execCommand (works on HTTP)
 * @param text The text to copy
 * @returns boolean indicating success
 */
function fallbackCopyToClipboard(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    // Make the textarea invisible but accessible
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    let successful = false;
    try {
        successful = document.execCommand('copy');
    }
    catch (err) {
        console.error('Fallback copy failed', err);
    }
    document.body.removeChild(textArea);
    return successful;
}
/**
 * Copy text to clipboard with error handling and fallback for HTTP
 * @param text The text to copy
 * @param successMessage Optional custom success message
 * @returns Promise resolving to success message or rejecting with error
 */
export async function copyToClipboard(text, successMessage) {
    console.log('📋 [Clipboard] Attempting to copy:', text.substring(0, 50) + (text.length > 50 ? '...' : ''));
    // Try modern clipboard API first (requires HTTPS)
    if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            console.log('✅ [Clipboard] Successfully copied using modern API');
            return successMessage || 'Copied to clipboard!';
        }
        catch (error) {
            console.warn('⚠️ [Clipboard] Modern clipboard API failed, falling back to execCommand:', error);
            console.info('💡 [Clipboard] Note: For production, consider using HTTPS to enable the modern clipboard API');
            // Fall through to fallback method
        }
    }
    else {
        console.info('💡 [Clipboard] Modern clipboard API not available (likely due to HTTP). Using fallback method.');
        console.info('💡 [Clipboard] For production, consider using HTTPS to enable the modern clipboard API');
    }
    // Fallback method for HTTP or when modern API fails
    console.log('🔧 [Clipboard] Using fallback method (execCommand)');
    const success = fallbackCopyToClipboard(text);
    if (success) {
        console.log('✅ [Clipboard] Successfully copied using fallback method');
        return successMessage || 'Copied to clipboard!';
    }
    else {
        console.error('❌ [Clipboard] Both modern and fallback methods failed');
        throw new Error('Copy failed: Neither modern nor fallback clipboard methods worked');
    }
}
/**
 * Copy text and return a formatted toast message
 * @param text The text to copy
 * @param label Optional label for the toast message
 * @returns Promise resolving to formatted toast message
 */
export async function copyWithToast(text, label) {
    if (label) {
        const message = `Copied ${label}: ${text}`;
        await copyToClipboard(text, message);
        return message;
    }
    const message = 'Copied! You can now paste it.';
    await copyToClipboard(text, message);
    return message;
}
